import { useEffect, useState } from "react";
import API from "../../../services/api";
import "./FeesDashboard.css";

export default function FeesDashboard() {
  const schoolAdmin = JSON.parse(localStorage.getItem("schoolAdmin"));

  const schoolId = schoolAdmin?.school?.id;

  const [data, setData] = useState(null);

  const [month, setMonth] = useState(
    new Date().toLocaleString("en-US", {
      month: "long",
    }),
  );

  const [selectedFee, setSelectedFee] = useState("");

  const [loading, setLoading] = useState(true);

  // =========================
  // LOAD DASHBOARD
  // =========================
  const loadData = async () => {
    try {
      setLoading(true);

      const res = await API.get("/school-admin/fees/dashboard/", {
        params: {
          month,
          addon: selectedFee,
        },

        headers: {
          "X-SCHOOL-ID": schoolId,
        },
      });

      setData({
        ...res.data,

        fee_summary: res.data.addon_summary || {},

        fee_students: res.data.addon_students || [],

        payment_settings: res.data.payment_settings || {},
      });
    } catch (err) {
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // AUTO LOAD
  // =========================
  useEffect(() => {
    if (schoolId) {
      loadData();
    }
  }, [month, selectedFee]);

  // =========================
  // RESET SELECTED FEE
  // =========================
  useEffect(() => {
    setSelectedFee("");
  }, [month]);

  // =========================
  // LIVE REFRESH
  // =========================
  useEffect(() => {
    const refreshDashboard = () => {
      loadData();
    };

    window.addEventListener("feesUpdated", refreshDashboard);

    return () => {
      window.removeEventListener("feesUpdated", refreshDashboard);
    };
  }, []);

  // =========================
  // TOGGLE PAYMENT MODE
  // =========================
  const togglePaymentMode = async () => {
    try {
      await API.post(
        "/school-admin/payment-mode/toggle/",
        {},
        {
          headers: {
            "X-SCHOOL-ID": schoolId,
          },
        },
      );

      loadData();
    } catch (err) {
      alert("Failed to change payment mode");
    }
  };

  // =========================
  // LOADING
  // =========================
  if (loading || !data) {
    return <p className="loading">Loading dashboard…</p>;
  }

  return (
    <div className="fees-dashboard-container">
      {/* ================= HEADER ================= */}
      <div className="fees-dashboard-header">
        <div>
          <h2>Fees Dashboard</h2>

          <p className="subtitle">
            Complete overview of student fee collection
          </p>

          <span
            className={`payment-badge ${
              data.payment_settings?.mode || "online"
            }`}
          >
            {data.payment_settings?.mode === "offline"
              ? "Offline Mode Active"
              : "Online Mode Active"}
          </span>
        </div>

        <div className="header-right">
          <button
            className={`toggle-btn ${
              data.payment_settings?.mode === "offline" ? "offline" : "online"
            }`}
            onClick={togglePaymentMode}
          >
            {data.payment_settings?.mode === "offline"
              ? "Switch to Online"
              : "Switch to Offline"}
          </button>

          <select value={month} onChange={(e) => setMonth(e.target.value)}>
            {[
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ].map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ================= SUMMARY ================= */}
      {/* ================= SUMMARY ================= */}
<div className="fees-summary-grid">

  <div className="card">
    <span>Total Students</span>
    <b>{data.total_students}</b>
  </div>

  <div className="card total">
    <span>Total Fees</span>
    <b>₹ {data.total_fee_amount}</b>
  </div>

  <div className="card amount">
    <span>Total Collected</span>
    <b>₹ {data.total_collected}</b>
  </div>

  <div className="card pending">
    <span>Pending Amount</span>
    <b>₹ {data.pending_amount}</b>
  </div>

</div>
      {/* ================= FEES ================= */}
      <h3 className="section-title">Fees</h3>

      <div className="addon-grid">
        {Object.keys(data.fee_summary).length === 0 && (
          <p className="empty">No fees recorded</p>
        )}

        {Object.entries(data.fee_summary).map(([title, v]) => (
          <div
            key={title}
            className={`addon-card ${selectedFee === title ? "active" : ""}`}
            onClick={() => setSelectedFee(selectedFee === title ? "" : title)}
          >
            <h4>{title}</h4>

            <p>{v.total} students</p>

            <div className="addon-amounts">
              <small>Total: ₹ {v.amount}</small>

              <b>Paid: ₹ {v.paid}</b>
            </div>
          </div>
        ))}
      </div>

      {/* ================= STUDENT LIST ================= */}
      {selectedFee && (
        <>
          <h3 className="section-title">{selectedFee} – Student List</h3>

          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Class</th>
                <th>Section</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Remaining</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {data.fee_students.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty">
                    No students found
                  </td>
                </tr>
              ) : (
                data.fee_students.map((s) => (
                  <tr key={s.id}>
                    <td>{s.student}</td>

                    <td>{s.class}</td>

                    <td>{s.section}</td>

                    <td>₹ {s.amount}</td>

                    <td>₹ {s.paid_amount}</td>

                    <td>₹ {s.remaining}</td>

                    <td>
                      <span
                        className={
                          s.status === "paid" ? "badge green" : "badge orange"
                        }
                      >
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
